import { Link, Head } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell from '../../components/layout/TeacherShell';
import { Card } from '../../components/ui';
import { WordCard } from '../../components/dictionary';
import { cn } from '../../lib/cn';

const typeColors = {
  prefix: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  root: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  suffix: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  bound: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  infix: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
};

export default function MorphemesShow({ morpheme, words }) {
  const { t } = useTranslation();
  return (
    <TeacherShell scrollTitle={<><Link href="/teacher/dictionary" className="hover:text-blue-600 dark:hover:text-blue-400">{t('dictionary.title')}</Link>{'  ›  '}{morpheme.morpheme}</>}>
      <Head title={`${morpheme.morpheme} - Morpheme`} />

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link
          href="/teacher/dictionary"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-6"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('dictionary.backToDictionary')}
        </Link>

        {/* Header */}
        <Card className="p-8 mb-8">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
            <div>
              <h1 className="text-4xl font-bold font-mono text-gray-900 dark:text-white mb-2">
                {morpheme.morpheme}
              </h1>
              {morpheme.meaning && (
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  {morpheme.meaning}
                </p>
              )}
            </div>
            <span className={cn(
              'px-3 py-1.5 text-sm font-medium rounded',
              typeColors[morpheme.type] || 'bg-gray-100 text-gray-800'
            )}>
              {morpheme.type}
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span>{morpheme.wordCount} words contain this morpheme</span>
            {morpheme.origin && (
              <>
                <span>•</span>
                <span>Origin: {morpheme.origin}</span>
              </>
            )}
          </div>
        </Card>

        {/* Words List */}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('dictionary.wordsWith')} "{morpheme.morpheme}"
        </h2>
        <div className="space-y-3">
          {words.map((word) => (
            <WordCard key={word.id} word={word} variant="list" />
          ))}
        </div>

        {words.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              No words found with this morpheme.
            </p>
          </div>
        )}
      </div>
    </TeacherShell>
  );
}
